const WORKFLOW_VIEW_OPTIONS = [
  { value: 'live', text: 'Live' },
  { value: 'wip', text: 'Workspace' },
  { value: 'review', text: 'Review' },
  { value: 'ready', text: 'Ready for Upload' }
]

export default {
  methods: {
    workflowViewOptionText (view) {
      if (!view) return ''
      if (view.value === 'live') return view.text
      return view.text + ' (' + Number(this.workflowBatchCounts[view.value] || 0) + ')'
    },
    applyPendingWorkflowSelection (view) {
      let selected = null
      const pending = this.pendingWorkflowSelection
      if (pending && pending.view === view) {
        selected = this.maps.find(map => {
          if (pending.batchID && Number(map.batchID || 0) === Number(pending.batchID)) return true
          return pending.mapName && String(map.name || '') === String(pending.mapName || '')
        }) || null
        this.pendingWorkflowSelection = null
      }
      const map = selected || this.maps[0]
      if (!map) return
      this.selectMapEntry(map)
    },
    selectWorkflowBatch (batch) {
      if (!batch) return
      this.pendingWorkflowSelection = {
        view: 'wip',
        mapName: batch.mapName,
        batchID: batch.id
      }
      this.workflowStackedMaps = false
      if (this.workflowView !== 'wip') {
        this.workflowView = 'wip'
        return
      }
      this.$nextTick(() => {
        this.applyPendingWorkflowSelection('wip')
      })
    },
    isWorkflowViewVisible (view) {
      return this.visibleWorkflowViewOptions.some(option => option.value === view)
    },
    workflowStatusLabel (status) {
      const match = WORKFLOW_VIEW_OPTIONS.find(option => option.value === status)
      return match ? match.text : String(status || '')
    },
    reviewDecisionLabel (review) {
      const name = String(review && review.reviewerName ? review.reviewerName : 'Unknown')
      const decision = String(review && review.decision ? review.decision : '').toLowerCase()
      if (decision === 'approved') return 'Approved by ' + name
      if (decision === 'rejected') return 'Rejected by ' + name
      return name
    },
    canRemoveReview (review, batch) {
      const sourceBatch = batch || this.activeBatch
      return !!(this.authUser && review && Number(review.reviewerID || 0) === Number(this.authUser.id || 0) && sourceBatch && sourceBatch.status === 'review')
    },
    removeOwnReview (batch) {
      if (!batch) return
      this.emit('removeSpawnBatchReview', { id: batch.id })
    },
    moveBatch (batch, status) {
      if (!batch) return
      if (!this.batchCanMoveToStatus(batch, status)) return
      this.emit('moveSpawnBatchStatus', { id: batch.id, status })
    },
    moveActiveBatch (status) {
      if (!this.activeBatch) return
      this.moveBatch(this.activeBatch, status)
    },
    moveStackedBatches (status) {
      const moved = this.stackedOwnedBatches
        .filter(batch => this.batchCanMoveToStatus(batch, status))
      if (!moved.length) return
      moved.forEach(batch => {
        this.moveBatch(batch, status)
      })
      this.pendingWorkflowSelection = {
        view: status,
        mapName: moved[0].mapName,
        batchID: moved[0].id
      }
      this.workflowStackedMaps = false
      if (this.workflowView !== status) {
        this.workflowView = status
      }
    },
    removeBatch (batch) {
      if (!this.batchCanRemoveOrMoveBack(batch)) return
      if (batch.status === 'wip' && !window.confirm('Remove this map from Workspace?')) return
      this.emit('removeSpawnBatch', { id: batch.id })
    },
    removeStackedWorkspaceBatches () {
      const batches = this.stackedOwnedWipBatches.filter(batch => this.batchCanRemoveOrMoveBack(batch))
      if (!batches.length) return
      if (!window.confirm('Remove all Workspace maps shown here?')) return
      batches.forEach(batch => {
        this.emit('removeSpawnBatch', { id: batch.id })
      })
    },
    moveBatchBackToWorkspace (batch) {
      if (!this.batchCanMoveBackToWorkspace(batch)) return
      this.emit('removeSpawnBatch', { id: batch.id })
    },
    moveStackedBatchesBackToWorkspace () {
      this.currentWorkflowBatches
        .filter(batch => this.batchCanMoveBackToWorkspace(batch))
        .forEach(batch => {
          this.moveBatchBackToWorkspace(batch)
        })
    },
    batchCanMoveToStatus (batch, status) {
      if (!batch || !batch.isOwner || !this.batchHasChanges(batch)) return false
      if (status === 'review') return batch.status === 'wip'
      if (status === 'ready') return batch.status === 'wip' || batch.status === 'review'
      return false
    },
    batchCanMoveBackToWorkspace (batch) {
      return !!(batch && batch.isOwner && (batch.status === 'review' || batch.status === 'ready'))
    },
    batchCanRemoveOrMoveBack (batch) {
      return !!(batch && batch.isOwner && (batch.status === 'wip' || batch.status === 'review' || batch.status === 'ready'))
    },
    batchHasChanges (batch) {
      return this.comparisonRowsForBatch(batch).length > 0
    },
    comparisonRowsForBatch (batch) {
      if (!batch) return []
      const before = this.liveSpawnsByMap[batch.mapName] || []
      const after = batch.spawns || []
      return this.buildComparisonRows(before, after)
    },
    removeActiveBatch () {
      if (!this.activeBatch) return
      this.removeBatch(this.activeBatch)
    },
    batchCanReview (batch) {
      return !!(batch && batch.canReview)
    },
    reviewBatch (batch, decision) {
      if (!this.batchCanReview(batch)) return
      this.emit('reviewSpawnBatch', { id: batch.id, decision, comment: '' })
    },
    reviewStackedBatches (decision) {
      this.stackedReviewableBatches.forEach(batch => {
        this.reviewBatch(batch, decision)
      })
    },
    reviewActiveBatch (decision) {
      this.reviewBatch(this.activeBatch, decision)
    },
    uploadActiveBatch () {
      if (!this.activeBatch) return
      this.uploadBatch(this.activeBatch)
    },
    uploadBatch (batch) {
      if (!batch || !this.batchHasChanges(batch)) return
      this.emit('uploadReadySpawnBatch', { id: batch.id })
    },
    uploadStackedReadyBatches () {
      this.currentWorkflowBatches.forEach(batch => {
        if (batch.status === 'ready' && this.batchHasChanges(batch)) {
          this.uploadBatch(batch)
        }
      })
    },
    rejectReadyActiveBatch () {
      if (!this.activeBatch) return
      this.rejectReadyBatch(this.activeBatch)
    },
    rejectReadyBatch (batch) {
      if (!batch || batch.status !== 'ready') return
      this.emit('rejectReadySpawnBatch', { id: batch.id })
    },
    rejectReadyStackedBatches () {
      this.currentWorkflowBatches
        .filter(batch => batch.status === 'ready')
        .forEach(batch => {
          this.emit('rejectReadySpawnBatch', { id: batch.id })
        })
    },
    comparisonRowsForActiveBatch () {
      return this.comparisonRowsForBatch(this.activeBatch)
    },
    buildComparisonRows (before, after) {
      const beforeMap = this.comparisonMap(before)
      const afterMap = this.comparisonMap(after)
      const rows = []
      Object.keys(afterMap).forEach(key => {
        if (!beforeMap[key]) {
          rows.push({ key: 'add-' + key, type: 'Added', area: this.areaLabel(afterMap[key]), pokemon: this.pokemonName(afterMap[key]), before: '-', beforeHtml: '-', after: this.compactSpawnSummary(afterMap[key]), afterHtml: this.compactSpawnSummaryHtml(afterMap[key]) })
          return
        }
        const beforeSummary = this.compactSpawnSummary(beforeMap[key])
        const afterSummary = this.compactSpawnSummary(afterMap[key])
        if (beforeSummary !== afterSummary) {
          rows.push({
            key: 'edit-' + key,
            type: 'Edited',
            area: this.areaLabel(afterMap[key]),
            pokemon: this.pokemonName(afterMap[key]),
            before: this.changedSpawnSummary(beforeMap[key], afterMap[key], 'before'),
            beforeHtml: this.changedSpawnSummaryHtml(beforeMap[key], afterMap[key], 'before'),
            after: this.changedSpawnSummary(beforeMap[key], afterMap[key], 'after'),
            afterHtml: this.changedSpawnSummaryHtml(beforeMap[key], afterMap[key], 'after')
          })
        }
      })
      Object.keys(beforeMap).forEach(key => {
        if (!afterMap[key]) {
          rows.push({ key: 'delete-' + key, type: 'Deleted', area: this.areaLabel(beforeMap[key]), pokemon: this.pokemonName(beforeMap[key]), before: this.compactSpawnSummary(beforeMap[key]), beforeHtml: this.compactSpawnSummaryHtml(beforeMap[key]), after: '-', afterHtml: '-' })
        }
      })
      const typeOrder = {
        Edited: 0,
        Deleted: 1,
        Added: 2
      }
      return rows.sort((a, b) => {
        const typeCompare = (typeOrder[a.type] === undefined ? 99 : typeOrder[a.type]) - (typeOrder[b.type] === undefined ? 99 : typeOrder[b.type])
        if (typeCompare !== 0) return typeCompare
        if (a.area !== b.area) return a.area === 'Land' ? -1 : 1
        return a.pokemon.localeCompare(b.pokemon, undefined, { numeric: true })
      })
    },
    comparisonMap (spawns) {
      const map = Object.create(null)
      ;(spawns || []).forEach((spawn, index) => {
        const key = Number(spawn.id || 0) > 0 ? this.areaLabel(spawn) + '-' + spawn.id : this.areaLabel(spawn) + '-' + spawn.dexID + '-' + index
        map[key] = spawn
      })
      return map
    },
    compactSpawnSummary (spawn) {
      return this.spawnSummaryParts(spawn)
        .filter(part => part.display)
        .map(part => part.value)
        .join(' | ')
    },
    compactSpawnSummaryHtml (spawn) {
      return this.spawnSummaryParts(spawn)
        .filter(part => part.display)
        .map(part => part.html || this.escapeHtml(part.value))
        .join(' | ')
    },
    changedSpawnSummary (before, after, side) {
      const beforeMap = this.spawnSummaryPartMap(before)
      const afterMap = this.spawnSummaryPartMap(after)
      const order = []
      this.spawnSummaryParts(after).concat(this.spawnSummaryParts(before)).forEach(part => {
        if (order.indexOf(part.key) === -1) order.push(part.key)
      })
      const changes = order.reduce((result, key) => {
        const beforePart = beforeMap[key]
        const afterPart = afterMap[key]
        if (!beforePart && !afterPart) return result
        const fallbackLabel = (afterPart || beforePart).label
        const beforeValue = beforePart ? beforePart.value : fallbackLabel + ' -'
        const afterValue = afterPart ? afterPart.value : fallbackLabel + ' -'
        if (beforeValue !== afterValue) result.push(side === 'before' ? beforeValue : afterValue)
        return result
      }, [])
      return changes.length ? changes.join(' | ') : this.compactSpawnSummary(side === 'before' ? before : after)
    },
    changedSpawnSummaryHtml (before, after, side) {
      const beforeMap = this.spawnSummaryPartMap(before)
      const afterMap = this.spawnSummaryPartMap(after)
      const order = []
      this.spawnSummaryParts(after).concat(this.spawnSummaryParts(before)).forEach(part => {
        if (order.indexOf(part.key) === -1) order.push(part.key)
      })
      const changes = order.reduce((result, key) => {
        const beforePart = beforeMap[key]
        const afterPart = afterMap[key]
        if (!beforePart && !afterPart) return result
        const fallbackLabel = (afterPart || beforePart).label
        const beforeValue = beforePart ? beforePart.value : fallbackLabel + ' -'
        const afterValue = afterPart ? afterPart.value : fallbackLabel + ' -'
        const beforeHtml = beforePart ? beforePart.html || this.escapeHtml(beforePart.value) : this.escapeHtml(fallbackLabel + ' -')
        const afterHtml = afterPart ? afterPart.html || this.escapeHtml(afterPart.value) : this.escapeHtml(fallbackLabel + ' -')
        if (beforeValue !== afterValue) result.push(side === 'before' ? beforeHtml : afterHtml)
        return result
      }, [])
      return changes.length ? changes.join(' | ') : this.compactSpawnSummaryHtml(side === 'before' ? before : after)
    },
    spawnSummaryPartMap (spawn) {
      return this.spawnSummaryParts(spawn).reduce((result, part) => {
        result[part.key] = part
        return result
      }, Object.create(null))
    },
    spawnSummaryParts (spawn) {
      const timeLabel = this.timeLabelForSpawn(spawn)
      const itemActive = Number(spawn.itemID || 0) > 0
      const water = this.isWaterSpawn(spawn)
      const fishOnly = water && this.isFishingOnly(spawn)
      const tierValue = this.censorRarityTiers ? this.censoredTierLabel() : this.tierDisplayLabel(this.rarityTier(spawn))
      const tierHtml = this.censorRarityTiers ? '<span class="spawn-censored-value">' + this.escapeHtml(tierValue) + '</span>' : this.escapeHtml(tierValue)
      const parts = [
        { key: 'level', label: 'Lv', value: this.formatSpawnLevelRange(spawn), display: true },
        { key: 'rarity', label: 'Tier', value: tierValue, html: tierHtml, display: true },
        { key: 'time', label: 'Time', value: timeLabel, display: true },
        { key: 'item', label: 'Item', value: itemActive ? this.itemName(spawn) + ': ' + this.itemDropLabel(spawn) : 'Item -', display: itemActive },
        { key: 'member', label: 'Membership', value: spawn.memberOnly ? 'Membership: Active' : 'Membership: Inactive', display: true }
      ]
      if (water) {
        parts.push({ key: 'rod', label: 'Rod', value: this.rodLabel(spawn), display: true })
        parts.push({ key: 'encounter', label: 'Encounter', value: fishOnly ? 'Fish-only' : 'Surf', display: fishOnly })
      }
      return parts
    },
  }
}
